import React, { useCallback, useEffect, useRef, useState } from 'react';
import Modal from 'react-modal';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import {
    CommentPermissionPage,
    CommentPermissionUser,
    deleteAdminUser,
    demotePremiumUser,
    fetchCommentPermissions,
    setCommentPermission,
} from '../api/commentPermissionApi';
import { errText, isMfaRequired } from '../api/errors';
import { selectMfa, selectMfaIsCurrent } from '../store/mfaSlice';
import type { RootState } from '../store/store';
import StepUpGate from './StepUpGate';

const EMPTY_PAGE: CommentPermissionPage = {
    users: [],
    page: 0,
    size: 20,
    totalElements: 0,
    totalPages: 0,
};

interface PendingAction {
    userId: number;
    allowed: boolean;
    token: string;
}

/** Keeps confirmed actions outside the gate so re-verification does not discard them. */
const AdminCommentModeration: React.FC = () => {
    const mfa = useSelector(selectMfa);
    const isCurrent = useSelector(selectMfaIsCurrent);
    const { accessToken, isAuthenticated, user } = useSelector((state: RootState) => state.auth);
    const canModerate = Boolean(isAuthenticated && user?.isAdmin && isCurrent
        && mfa.loadState === 'ready' && mfa.enabled && mfa.sessionVerified);
    const [draftQuery, setDraftQuery] = useState('');
    const [query, setQuery] = useState('');
    const [pageNumber, setPageNumber] = useState(0);
    const [searchRevision, setSearchRevision] = useState(0);
    const [result, setResult] = useState<CommentPermissionPage>(EMPTY_PAGE);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [notice, setNotice] = useState<string | null>(null);
    const [confirming, setConfirming] = useState<CommentPermissionUser | null>(null);
    const [demoting, setDemoting] = useState<CommentPermissionUser | null>(null);
    const [deleting, setDeleting] = useState<CommentPermissionUser | null>(null);
    const [deleteEmail, setDeleteEmail] = useState('');
    const [savingUserId, setSavingUserId] = useState<number | null>(null);
    const [pendingAfterStepUp, setPendingAfterStepUp] = useState<PendingAction | null>(null);
    const alive = useRef(true);
    const currentToken = useRef(accessToken);
    currentToken.current = accessToken;
    const saving = useRef(false);
    const listGeneration = useRef(0);

    useEffect(() => {
        alive.current = true;
        return () => { alive.current = false; };
    }, []);

    useEffect(() => {
        setPendingAfterStepUp(null);
        setConfirming(null);
        setDemoting(null);
        setDeleting(null);
        setDeleteEmail('');
        setResult(EMPTY_PAGE);
        setNotice(null);
        setError(null);
    }, [accessToken]);

    useEffect(() => {
        if (!canModerate) {
            return;
        }
        let cancelled = false;
        const generation = ++listGeneration.current;
        const isLatest = () => !cancelled && generation === listGeneration.current
            && currentToken.current === accessToken;
        setLoading(true);
        setError(null);
        const load = async () => {
            try {
                const data = await fetchCommentPermissions(query, pageNumber);
                if (isLatest()) {
                    setResult(data);
                }
            } catch (err) {
                if (isLatest()) {
                    setError(errText(err, 'Nepodarilo sa načítať oprávnenia na komentovanie.'));
                }
            } finally {
                if (isLatest()) {
                    setLoading(false);
                }
            }
        };
        void load();
        // A search, page change, or session change invalidates this particular response.
        return () => { cancelled = true; };
    }, [query, pageNumber, searchRevision, canModerate, accessToken]);

    const update = useCallback(async (action: PendingAction, mayWaitForStepUp: boolean) => {
        if (saving.current || currentToken.current !== action.token) {
            return;
        }
        saving.current = true;
        // A list fetched before this decision may still report the old permission.
        listGeneration.current += 1;
        setLoading(false);
        setSavingUserId(action.userId);
        setError(null);
        setNotice(null);
        try {
            const changed = await setCommentPermission(action.userId, action.allowed);
            if (!alive.current || currentToken.current !== action.token) {
                return;
            }
            setResult((current) => ({
                ...current,
                users: current.users.map((user) => user.id === changed.id ? changed : user),
            }));
            setNotice(action.allowed
                ? `Komentovanie pre ${changed.fullName} bolo povolené.`
                : `Komentovanie pre ${changed.fullName} bolo zablokované.`);
        } catch (err) {
            if (!alive.current || currentToken.current !== action.token) {
                return;
            }
            if (mayWaitForStepUp && isMfaRequired(err)) {
                // The shared interceptor opens the TOTP modal. Keep the already-confirmed action and
                // execute it once after that exact session becomes verified.
                setPendingAfterStepUp(action);
                return;
            }
            if (alive.current) {
                setError(errText(err, 'Nepodarilo sa zmeniť oprávnenie na komentovanie.'));
            }
        } finally {
            saving.current = false;
            if (alive.current) {
                setSavingUserId(null);
            }
        }
    }, []);

    useEffect(() => {
        if (!canModerate || !pendingAfterStepUp || pendingAfterStepUp.token !== accessToken) {
            return;
        }
        const action = pendingAfterStepUp;
        setPendingAfterStepUp(null);
        void update(action, false);
    }, [canModerate, accessToken, pendingAfterStepUp, update]);

    const submitSearch = (event: React.FormEvent) => {
        event.preventDefault();
        if (saving.current) {
            return;
        }
        setPageNumber(0);
        setQuery(draftQuery.trim());
        // Submitting the same query is also a retry after a network failure.
        setSearchRevision((value) => value + 1);
    };

    const confirmChange = () => {
        if (!confirming || !canModerate || !accessToken) {
            return;
        }
        const action = { userId: confirming.id, allowed: !confirming.canComment, token: accessToken };
        setConfirming(null);
        void update(action, true);
    };

    const canManageAccount = (target: CommentPermissionUser) =>
        Boolean(user && target.id !== user.id && !target.isAdmin);

    const demote = async () => {
        if (!demoting || !canModerate || !accessToken || !canManageAccount(demoting) || saving.current) return;
        const target = demoting;
        const token = accessToken;
        setDemoting(null);
        saving.current = true;
        listGeneration.current += 1;
        setLoading(false);
        setSavingUserId(target.id);
        setError(null);
        setNotice(null);
        try {
            const changed = await demotePremiumUser(target.id);
            if (!alive.current || currentToken.current !== token) return;
            setResult((current) => ({ ...current, users: current.users.map((row) => row.id === changed.id ? changed : row) }));
            setNotice(`Členstvo Premium používateľa ${changed.fullName} bolo okamžite odstránené, automatické obnovenie zrušené a používateľ odhlásený. Refundácia sa nevykonáva automaticky.`);
        } catch (err) {
            if (alive.current && currentToken.current === token) {
                setError(isMfaRequired(err)
                    ? 'Overenie druhým faktorom je potrebné. Po jeho dokončení potvrďte akciu znova.'
                    : errText(err, 'Nepodarilo sa odstrániť členstvo Premium.'));
            }
        } finally {
            saving.current = false;
            if (alive.current) setSavingUserId(null);
        }
    };

    const removeUser = async () => {
        if (!deleting || !canModerate || !accessToken || !canManageAccount(deleting)
            || deleteEmail !== deleting.email || saving.current) return;
        const target = deleting;
        const token = accessToken;
        setDeleting(null);
        setDeleteEmail('');
        saving.current = true;
        listGeneration.current += 1;
        setLoading(false);
        setSavingUserId(target.id);
        setError(null);
        setNotice(null);
        try {
            await deleteAdminUser(target.id, target.email);
            if (!alive.current || currentToken.current !== token) return;
            const remainingCount = Math.max(0, result.totalElements - 1);
            const nextPageCount = Math.ceil(remainingCount / result.size);
            const nextPage = Math.max(0, Math.min(pageNumber, nextPageCount - 1));
            setResult((current) => ({
                ...current,
                users: current.users.filter((row) => row.id !== target.id),
                totalElements: Math.max(0, current.totalElements - 1),
                totalPages: Math.ceil(Math.max(0, current.totalElements - 1) / current.size),
                page: nextPage,
            }));
            setPageNumber(nextPage);
            if (nextPage === pageNumber) setSearchRevision((value) => value + 1);
            setNotice(`Účet ${target.email} a osobné údaje boli odstránené. Príspevky a diskusie zostávajú anonymizované; faktúry Stripe zostávajú zachované.`);
        } catch (err) {
            if (alive.current && currentToken.current === token) {
                setError(isMfaRequired(err)
                    ? 'Overenie druhým faktorom je potrebné. Po jeho dokončení potvrďte akciu znova.'
                    : errText(err, 'Nepodarilo sa odstrániť účet.'));
            }
        } finally {
            saving.current = false;
            if (alive.current) setSavingUserId(null);
        }
    };

    return (
        <StepUpGate strict>
        <Panel aria-labelledby="comment-moderation-title">
            <HeadingRow>
                <div>
                    <SectionTitle id="comment-moderation-title">Správa používateľov</SectionTitle>
                    <Description>
                        Spravujte členstvo a účty, prípadne zablokujte alebo povoľte komentovanie. Každá zmena vyžaduje overenú 2FA reláciu.
                    </Description>
                </div>
                <Count>{result.totalElements} používateľov</Count>
            </HeadingRow>

            <SearchForm onSubmit={submitSearch} role="search">
                <SearchInput
                    value={draftQuery}
                    disabled={savingUserId !== null}
                    onChange={(event) => setDraftQuery(event.target.value)}
                    placeholder="Meno alebo e-mail"
                    aria-label="Vyhľadať používateľa podľa mena alebo e-mailu"
                />
                <PrimaryButton type="submit" disabled={savingUserId !== null}>Hľadať</PrimaryButton>
                {(query || draftQuery) && (
                    <SecondaryButton type="button" disabled={savingUserId !== null} onClick={() => {
                        setDraftQuery('');
                        setQuery('');
                        setPageNumber(0);
                    }}>
                        Zrušiť filter
                    </SecondaryButton>
                )}
            </SearchForm>

            {error && <Message role="alert" $error>{error}</Message>}
            {notice && <Message role="status">{notice}</Message>}

            {loading ? (
                <Empty>Načítavam používateľov…</Empty>
            ) : result.users.length === 0 ? (
                <Empty>Žiadni používatelia nezodpovedajú vyhľadávaniu.</Empty>
            ) : (
                <TableScroll>
                    <Table>
                        <thead>
                        <tr>
                            <th>Meno</th>
                            <th>E-mail</th>
                            <th>Členstvo</th>
                            <th>Komentovanie</th>
                            <th>Zmeniť</th>
                            <th>Účet</th>
                        </tr>
                        </thead>
                        <tbody>
                        {result.users.map((user) => (
                            <tr key={user.id}>
                                <td><strong>{user.fullName}</strong></td>
                                <td><Email>{user.email}</Email></td>
                                <td>{user.isPremium ? <Premium>Premium</Premium> : 'Základné'}</td>
                                <td>
                                    <Status $allowed={user.canComment}>
                                        {user.canComment ? 'Povolené' : 'Zablokované'}
                                    </Status>
                                </td>
                                <td>
                                    <ActionGroup><ActionButton
                                        type="button"
                                        $allow={!user.canComment}
                                        disabled={savingUserId !== null || Boolean(pendingAfterStepUp)}
                                        onClick={() => setConfirming(user)}
                                    >
                                        {savingUserId === user.id
                                            ? 'Ukladám…'
                                            : user.canComment ? 'Zablokovať' : 'Povoliť'}
                                    </ActionButton></ActionGroup>
                                </td>
                                <td>
                                    {canManageAccount(user) && <ActionGroup>
                                        {user.isPremium && <AccountActionButton type="button"
                                            disabled={savingUserId !== null || Boolean(pendingAfterStepUp)}
                                            onClick={() => setDemoting(user)}>Odobrať Premium</AccountActionButton>}
                                        <DeleteButton type="button" disabled={savingUserId !== null || Boolean(pendingAfterStepUp)}
                                            onClick={() => { setDeleteEmail(''); setDeleting(user); }}>Odstrániť účet</DeleteButton>
                                    </ActionGroup>}
                                </td>
                            </tr>
                        ))}
                        </tbody>
                    </Table>
                </TableScroll>
            )}

            {result.totalPages > 1 && (
                <Pagination aria-label="Stránkovanie používateľov">
                    <SecondaryButton
                        type="button"
                        disabled={pageNumber === 0 || loading || savingUserId !== null}
                        onClick={() => setPageNumber((value) => Math.max(0, value - 1))}
                    >
                        Predchádzajúca
                    </SecondaryButton>
                    <span>Strana {result.page + 1} z {result.totalPages}</span>
                    <SecondaryButton
                        type="button"
                        disabled={pageNumber + 1 >= result.totalPages || loading || savingUserId !== null}
                        onClick={() => setPageNumber((value) => value + 1)}
                    >
                        Ďalšia
                    </SecondaryButton>
                </Pagination>
            )}

            <Modal
                isOpen={canModerate && Boolean(confirming)}
                onRequestClose={() => setConfirming(null)}
                contentLabel="Potvrdenie zmeny komentovania"
                ariaHideApp={false}
                style={modalStyles}
            >
                {confirming && (
                    <ConfirmCard>
                        <ConfirmTitle>
                            {confirming.canComment ? 'Zablokovať komentovanie?' : 'Povoliť komentovanie?'}
                        </ConfirmTitle>
                        <ConfirmText>
                            {confirming.fullName} ({confirming.email})
                            {confirming.canComment
                                ? ' nebude môcť pridávať nové komentáre.'
                                : ' bude môcť znova pridávať komentáre.'}
                        </ConfirmText>
                        <ConfirmActions>
                            <SecondaryButton type="button" onClick={() => setConfirming(null)}>
                                Zrušiť
                            </SecondaryButton>
                            <ActionButton
                                type="button"
                                $allow={!confirming.canComment}
                                onClick={confirmChange}
                            >
                                {confirming.canComment ? 'Zablokovať' : 'Povoliť'}
                            </ActionButton>
                        </ConfirmActions>
                    </ConfirmCard>
                )}
            </Modal>

            <Modal isOpen={canModerate && Boolean(demoting)} onRequestClose={() => setDemoting(null)}
                contentLabel="Potvrdenie odstránenia členstva Premium" ariaHideApp={false} style={modalStyles}>
                {demoting && <ConfirmCard>
                    <ConfirmTitle>Okamžite odobrať Premium?</ConfirmTitle>
                    <ConfirmText>
                        Používateľovi {demoting.fullName} ({demoting.email}) sa okamžite odoberie Premium,
                        zruší sa automatické obnovenie a používateľ bude odhlásený. Refundácia sa nevykoná automaticky.
                    </ConfirmText>
                    <ConfirmActions>
                        <SecondaryButton type="button" onClick={() => setDemoting(null)}>Zrušiť</SecondaryButton>
                        <DeleteButton type="button" disabled={savingUserId !== null} onClick={() => void demote()}>
                            Potvrdiť odobratie Premium
                        </DeleteButton>
                    </ConfirmActions>
                </ConfirmCard>}
            </Modal>

            <Modal isOpen={canModerate && Boolean(deleting)} onRequestClose={() => { setDeleting(null); setDeleteEmail(''); }}
                contentLabel="Potvrdenie trvalého odstránenia účtu" ariaHideApp={false} style={modalStyles}>
                {deleting && <ConfirmCard>
                    <ConfirmTitle>Trvalo odstrániť účet?</ConfirmTitle>
                    <ConfirmText>
                        Táto akcia je nevratná. Účet a osobné údaje budú odstránené. Aktívne predplatné sa zruší
                        okamžite, automatické obnovenie sa zastaví a člen bude odhlásený. Refundácia sa nevykoná
                        automaticky. Existujúce diskusné vlákna a zverejnené príspevky budú anonymizované a faktúry Stripe zostanú zachované.
                        Zadajte presne e-mail <strong>{deleting.email}</strong> na potvrdenie.
                    </ConfirmText>
                    <ConfirmInput aria-label="Potvrďte e-mail používateľa" value={deleteEmail}
                        onChange={(event) => setDeleteEmail(event.target.value)} autoComplete="off" />
                    <ConfirmActions>
                        <SecondaryButton type="button" onClick={() => { setDeleting(null); setDeleteEmail(''); }}>Zrušiť</SecondaryButton>
                        <DeleteButton type="button" disabled={deleteEmail !== deleting.email || savingUserId !== null}
                            onClick={() => void removeUser()}>
                            Trvalo odstrániť účet
                        </DeleteButton>
                    </ConfirmActions>
                </ConfirmCard>}
            </Modal>
        </Panel>
        </StepUpGate>
    );
};

const modalStyles = {
    overlay: {
        backgroundColor: 'rgba(0, 0, 0, 0.7)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px',
        zIndex: 1000,
    },
    content: {
        position: 'relative' as const,
        inset: 'auto',
        width: '100%',
        maxWidth: '480px',
        padding: '0',
        border: 'none',
        borderRadius: '12px',
        overflow: 'visible',
    },
};

const Panel = styled.section`
    box-sizing: border-box;
    width: 100%;
    max-width: 1280px;
    margin-left: auto;
    margin-right: auto;
    margin-top: 28px;
    padding: 24px;
    background: #fff;
    border: 1px solid #ece8e4;
    border-radius: 12px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.07);
`;

const HeadingRow = styled.div`
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 20px;

    @media (max-width: 680px) {
        flex-direction: column;
    }
`;

const SectionTitle = styled.h2`
    margin: 0 0 6px;
    color: #28231f;
    font-size: 22px;
`;

const Description = styled.p`
    margin: 0;
    color: #6e665f;
    font-size: 14px;
    line-height: 1.5;
`;

const Count = styled.span`
    flex: 0 0 auto;
    color: #7d756f;
    font-size: 13px;
    padding-top: 5px;
`;

const SearchForm = styled.form`
    display: flex;
    gap: 10px;
    margin: 22px 0 16px;

    @media (max-width: 680px) {
        flex-direction: column;
    }
`;

const SearchInput = styled.input`
    min-width: 0;
    flex: 1;
    padding: 11px 13px;
    border: 1px solid #cfc8c2;
    border-radius: 7px;
    font: inherit;

    &:focus {
        outline: 3px solid rgba(245, 102, 7, 0.2);
        border-color: #f56607;
    }
`;

const BaseButton = styled.button`
    border-radius: 7px;
    padding: 10px 16px;
    border: 1px solid transparent;
    font: inherit;
    font-weight: 700;
    cursor: pointer;

    &:disabled {
        opacity: 0.55;
        cursor: default;
    }
`;

const PrimaryButton = styled(BaseButton)`
    color: #fff;
    background: #f56607;
`;

const SecondaryButton = styled(BaseButton)`
    color: #332f2c;
    background: #fff;
    border-color: #d7d1cc;
`;

const Message = styled.p<{ $error?: boolean }>`
    margin: 12px 0;
    padding: 11px 13px;
    border-radius: 7px;
    color: ${(p) => p.$error ? '#8c1d18' : '#256029'};
    background: ${(p) => p.$error ? '#fdecea' : '#edf7ed'};
`;

const Empty = styled.p`
    padding: 22px 0;
    color: #777;
    text-align: center;
`;

const TableScroll = styled.div`
    overflow-x: auto;
`;

const Table = styled.table`
    width: 100%;
    border-collapse: collapse;
    min-width: 720px;

    th, td {
        padding: 13px 10px;
        border-bottom: 1px solid #eee9e5;
        text-align: left;
        vertical-align: middle;
    }

    th {
        color: #756d66;
        font-size: 12px;
        text-transform: uppercase;
        letter-spacing: 0.05em;
    }

    td:last-child, th:last-child {
        text-align: right;
    }

    td { overflow-wrap: anywhere; }
`;

const Email = styled.span`
    overflow-wrap: anywhere;
    word-break: break-word;
`;

const ActionGroup = styled.div`
    display: flex;
    flex-wrap: wrap;
    justify-content: flex-end;
    gap: 7px;
    min-width: 150px;
`;

const Premium = styled.span`
    color: #8b5400;
    background: #fff2d7;
    border-radius: 999px;
    padding: 4px 8px;
    font-size: 12px;
    font-weight: 700;
`;

const Status = styled.span<{ $allowed: boolean }>`
    color: ${(p) => p.$allowed ? '#256029' : '#8c1d18'};
    background: ${(p) => p.$allowed ? '#edf7ed' : '#fdecea'};
    border-radius: 999px;
    padding: 5px 9px;
    font-size: 12px;
    font-weight: 700;
`;

const ActionButton = styled(BaseButton)<{ $allow: boolean }>`
    color: #fff;
    background: ${(p) => p.$allow ? '#2f7d32' : '#b3261e'};
`;

const AccountActionButton = styled(BaseButton)`
    color: #fff;
    background: #6d5b00;
`;

const DeleteButton = styled(BaseButton)`
    color: #fff;
    background: #9b1c1c;
`;

const Pagination = styled.nav`
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 14px;
    margin-top: 18px;
    color: #655e58;
`;

const ConfirmCard = styled.div`
    padding: 24px;
    background: #fff;
    border-radius: 12px;
`;

const ConfirmTitle = styled.h2`
    margin: 0 0 12px;
    color: #28231f;
`;

const ConfirmText = styled.p`
    margin: 0;
    color: #5f5852;
    line-height: 1.6;
`;

const ConfirmInput = styled.input`
    box-sizing: border-box;
    width: 100%;
    margin-top: 16px;
    padding: 11px 13px;
    border: 1px solid #b9b1aa;
    border-radius: 7px;
    font: inherit;
`;

const ConfirmActions = styled.div`
    display: flex;
    justify-content: flex-end;
    gap: 10px;
    margin-top: 22px;

    @media (max-width: 520px) {
        flex-direction: column-reverse;
        > button { width: 100%; }
    }
`;

export default AdminCommentModeration;
